<template>
  <div class="mind-map" ref="container"></div>
</template>

<script lang="ts">
import Vue, { PropType, VNode } from "vue";
import {
  MindMap,
  MindMapEvents,
  MindMapNodeDestroyEvent,
  TreeNode,
  FlatNode,
  NodeLike,
  MindMapAlign,
  MindMapStyle,
  MindMapDragOptions,
  MindMapZoomOptions,
  FullscreenOptions,
  DebugOptions,
  // DEFAULT_FULLSCREEN_OPTIONS,
  // DEFAULT_DRAG_OPTIONS,
  // DEFAULT_ZOOM_OPTIONS,
} from "@/map/mind-map/entities/MindMap";
import { getRamdomStr } from "@/map/utils/StringUtils";

type LineShapeType = "straight" | "curved" | "zigzag";

const NODE_INNER_WRAPPER_CLASS = "node-inner-wrapper";

export default Vue.extend({
  name: "MindMap",
  props: {
    /**
     * 是否启用调试模式
     * 在调试模式下，组件会输出更多的日志信息，便于开发和调试
     */
    isDebug: { type: Boolean, default: false },
    /**
     * 思维导图数据，定义了节点结构和内容
     * 必须是符合 TreeNode 接口的对象或 FlatNode 数组
     */
    data: {
      type: [Object, Array] as PropType<TreeNode | FlatNode[]>,
      required: true,
      validator(value: any) {
        if (Array.isArray(value)) {
          return value.every((item) => item && typeof item.id !== "undefined");
        }
        return value && typeof value.id !== "undefined";
      },
    },
    /**
     * 思维导图布局方向
     * - horizontal: 水平布局，根节点在左侧，子节点向右展开
     * - vertical: 垂直布局，根节点在顶部，子节点向下展开
     */
    direction: {
      type: String as PropType<"horizontal" | "vertical">,
      default: "horizontal",
    },
    /**
     * 连接线样式
     * - straight: 直线
     * - curved: 曲线
     * - zigzag: 折线
     */
    lineStyle: {
      type: String as PropType<LineShapeType>,
      default: "curved",
    },
    /**
     * 连接线颜色，支持所有有效的 CSS 颜色值
     */
    lineColor: { type: String, default: "#000000" },
    /**
     * 连接线宽度，单位为像素
     */
    lineWidth: { type: Number, default: 1 },
    /**
     * 节点边框宽度，单位为像素
     */
    nodeBorderWidth: { type: Number, default: 1 },
    /**
     * 节点背景颜色，支持所有有效的 CSS 颜色值
     */
    nodeBgColor: { type: String, default: "#FFFFFF" },
    /**
     * 节点边框颜色，支持所有有效的 CSS 颜色值
     */
    nodeBorderColor: { type: String, default: "#000000" },
    /**
     * 思维导图对齐方式配置
     * - mode: 对齐模式
     *   - cross-axis-start-edge: 交叉轴起始边与视口起始边对齐
     *   - cross-axis-center: 交叉轴居中对齐到视口中心
     *   - node-center: 指定节点中心对齐到视口中心
     * - targetNodeId: 目标节点ID (仅在 node-center 模式下使用)
     */
    align: {
      type: Object as PropType<MindMapAlign>,
      default: () => ({ mode: "cross-axis-start-edge" }),
    },
    /**
     * 同级节点之间的间距，单位为像素
     * 指同级节点边界之间的最小距离
     */
    siblingSeparation: { type: Number, default: 25 },
    /**
     * 层级间的间距，单位为像素
     * 指不同层级节点边界之间的最小距离
     */
    levelSeparation: { type: Number, default: 50 },
    /**
     * 思维导图的初始偏移量
     * 可以指定 x 和/或 y 坐标值
     */
    initialOffset: { type: Object as PropType<{ x?: number; y?: number }> },
    /**
     * 全屏相关配置
     * - fullscreenBgColor: 全屏模式下的背景颜色，默认为 '#FFFFFF'
     * - fullscreenMode: 全屏模式类型，默认为 'native'
     *   - 'native': 使用浏览器原生全屏 API
     *   - 'pseudo': 使用 CSS 模拟全屏效果
     */
    fullScreenOptions: {
      type: Object as PropType<FullscreenOptions>,
    },
    /**
     * 拖拽相关配置
     * - enableNodeDrag: 是否允许拖拽节点，默认为 true
     * - enableCanvasDrag: 是否允许拖拽画布，默认为 true
     * - dragNodeButton: 用于拖拽节点的鼠标按键，默认为左键
     * - dragCanvasButton: 用于拖拽画布的鼠标按键，默认为右键和中键
     * - useThrottle: 是否使用节流减少拖拽事件频率，默认为 true
     * - throttleWait: 节流函数的等待时间 (ms)，默认为 16
     */
    dragOptions: {
      type: Object as PropType<MindMapDragOptions>,
    },
    /**
     * 缩放相关配置
     * - enableWheelZoom: 是否启用滚轮缩放，默认为 true
     * - zoomStep: 每次缩放的步长，如 0.1 表示放大/缩小 10%，默认为 0.1
     * - invertWheelDirection: 是否反转滚轮方向，默认为 false
     * - minZoom: 最小缩放比例，如 0.1 表示最小可缩小到 10%，默认为 0.1
     * - maxZoom: 最大缩放比例，如 5.0 表示最大可放大到 500%，默认为 5.0
     * - useThrottle: 是否使用节流减少缩放事件频率，默认为 true
     * - throttleWait: 节流函数的等待时间 (ms)，默认为 16
     */
    zoomOptions: {
      type: Object as PropType<MindMapZoomOptions>,
    },
  },
  data() {
    return {
      // 存储 MindMap 实例
      mindMap: null as MindMap | null,
      // 存储节点 ID 到 Vue 实例的映射
      nodeIdToVueInstanceMap: new Map<string, Vue>(),
      debugOptions: null as DebugOptions | null,
    };
  },
  watch: {
    data: {
      handler(newData: TreeNode | FlatNode[]) {
        if (this.mindMap) {
          // 如果 mindMap 实例已存在，则调用 setRootNodeData 更新数据
          this.mindMap.setRootNodeData(newData);
        }
        // 如果 mindMap 实例尚未创建（例如，在 mounted 之前 prop 就绪），
        // _initializeMindMap 将在 mounted 时使用最新的 this.data。
      },
      deep: false, // 只监听数据对象的引用变化，不深度监听
    },
  },
  mounted() {
    // 在组件挂载后初始化 MindMap 实例
    this._initializeMindMap();
    // 监听节点销毁事件
    // this._setupNodeDestroyListener();
  },
  beforeDestroy() {
    // 在组件销毁前清理 MindMap 实例
    this._destroyMindMapInstance();
  },
  methods: {
    // #region 公共方法

    /**
     * 重置思维导图布局并重新渲染
     */
    rebuild(): void {
      this.mindMap?.rebuild();
    },

    /**
     * 清空思维导图实例的所有内容和状态
     */
    clear() {
      this.mindMap?.clear();
    },

    /**
     * 设置思维导图布局方向
     * @param isHorizontal - 是否水平布局
     */
    setHorizontal(isHorizontal: boolean): void {
      this.mindMap?.setHorizontal(isHorizontal);
    },

    /**
     * 放大思维导图
     * @param cx - 缩放中心点的 X 坐标
     * @param cy - 缩放中心点的 Y 坐标
     */
    zoomIn(cx?: number, cy?: number): void {
      this.mindMap?.zoomIn(cx, cy);
    },

    /**
     * 缩小思维导图
     * @param cx - 缩放中心点的 X 坐标
     * @param cy - 缩放中心点的 Y 坐标
     */
    zoomOut(cx?: number, cy?: number): void {
      this.mindMap?.zoomOut(cx, cy);
    },

    /**
     * 重置思维导图缩放比例
     * @param cx - 缩放中心点的 X 坐标
     * @param cy - 缩放中心点的 Y 坐标
     */
    resetZoom(cx?: number, cy?: number): void {
      this.mindMap?.resetZoom(cx, cy);
    },

    /**
     * 设置指定节点的尺寸
     * @param nodeId - 要设置尺寸的节点 ID
     * @param width - 新的宽度
     * @param height - 新的高度
     * @returns 如果节点找到并成功设置尺寸，则返回 true；否则返回 false
     */
    setNodeSize(
      nodeId: string,
      size: { width?: number; height?: number }
    ): boolean | undefined {
      return this.mindMap?.setNodeSize(nodeId, size);
    },

    /**
     * 将指定 ID 的节点聚焦到视口中心
     * @param nodeId - 要聚焦的节点的 ID
     * @returns 如果节点找到并成功聚焦，则返回 true；否则返回 false
     */
    focusOnNode(nodeId: string): boolean | undefined {
      return this.mindMap?.focusOnNode(nodeId);
    },

    /**
     * 切换节点的折叠状态
     * @param nodeId - 要切换折叠状态的节点 ID
     * @returns 如果节点找到并成功切换状态，则返回 true；否则返回 false
     */
    toggleNodeCollapse(nodeId: string): boolean | undefined {
      return this.mindMap?.toggleNodeCollapse(nodeId);
    },

    /**
     * 设置节点的折叠状态
     * @param nodeId - 要设置折叠状态的节点 ID
     * @param collapsed - 要设置的折叠状态
     * @returns 如果节点找到并成功设置状态，则返回 true；否则返回 false
     */
    setNodeCollapsed(nodeId: string, collapsed: boolean): boolean | undefined {
      return this.mindMap?.setNodeCollapsed(nodeId, collapsed);
    },

    /**
     * 获取节点的折叠状态
     * @param nodeId - 要查询的节点 ID
     * @returns 节点的折叠状态，如果节点不存在则返回 undefined
     */
    getNodeCollapsed(nodeId: string): boolean | undefined {
      return this.mindMap?.getNodeCollapsed(nodeId);
    },

    /**
     * 展开所有节点
     * @returns 成功展开的节点数量
     */
    expandAllNodes(): number {
      return this.mindMap?.expandAllNodes() ?? 0;
    },

    /**
     * 折叠所有非根节点
     * @returns 成功折叠的节点数量
     */
    collapseAllNodes(): number {
      return this.mindMap?.collapseAllNodes() ?? 0;
    },

    /**
     * 更新指定节点的内容
     * 此方法会根据传入的节点 ID 数组，并根据节点自身的 `content` 属性或外部传入的 `contentCallback`
     * 来重新解析和设置节点内部的内容。
     * @param nodeIds - 要更新内容的节点 ID 数组，不传参时默认更新所有节点
     * @returns 成功更新的节点个数
     */
    async updateNodeContents(nodeIds: string[] = []): Promise<number> {
      await this.$nextTick();
      // 确保在 DOM 更新后再调用更新方法
      return this.mindMap?.updateNodeContents(nodeIds) || 0;
    },

    /**
     * 获取内部 MindMap 实例 (谨慎使用)
     * @returns MindMap instance or null
     */
    getMindMapInstance(): MindMap | null {
      return this.mindMap;
    },

    /**
     * 进入全屏模式
     * @param mode - 全屏模式 ('native' 或 'pseudo')。如果未提供，则使用组件 prop 中配置的默认模式
     * @returns Promise，成功时 resolve true，失败时 reject error
     */
    async enterFullscreen(
      mode?: "native" | "pseudo"
    ): Promise<boolean | undefined> {
      return this.mindMap?.enterFullscreen(mode);
    },

    /**
     * 退出全屏模式
     * 会自动检测并退出当前激活的全屏模式 (原生或模拟)
     * @returns Promise，成功时 resolve true，失败时 reject error
     */
    async exitFullscreen(): Promise<boolean | undefined> {
      return this.mindMap?.exitFullscreen();
    },

    /**
     * 切换全屏状态
     * @param mode - 全屏模式 ('native' 或 'pseudo')。如果未提供，则使用组件 prop 中配置的默认模式
     * @returns Promise，成功时 resolve 新的全屏状态 (true 为进入，false 为退出)
     */
    async toggleFullscreen(
      mode?: "native" | "pseudo"
    ): Promise<boolean | undefined> {
      return this.mindMap?.toggleFullscreen(mode);
    },

    /**
     * 输出思维导图容器的调试信息
     * @private
     */
    debug() {
      this.mindMap?.debug();
    },

    // #endregion 公共方法

    /**
     * 私有方法主要调用链路 (直接渲染插槽内容)
     *
     * 1. 初始化链路
     * mounted()
     *   └── _initializeMindMap()
     *       ├── _checkInitializationRequirements()
     *       ├── _destroyMindMapInstance()
     *       └── _createMindMapOptions()
     *           ├── _createNodeContentCallback()
     *           └── _createMindMapStyle()
     *
     * 2. 节点内容渲染链路
     * _createNodeContentCallback() 返回的回调函数
     *   └── _renderNodeWithSlot(node)
     *       ├── _getOrCreateNodeId(node)
     *       └── _createErrorDisplayElement() (渲染错误时)
     *       └── _renderDefaultNode(node) (回退渲染)
     *
     * 3. 清理链路
     * beforeDestroy()
     *   └── _destroyMindMapInstance()
     *
     * _setupNodeDestroyListener() 设置的事件监听
     *   └── _handleNodeDestroy(node)
     *       ├── _checkNodeExistsInCurrentData(nodeId)
     *       │   └── _findNodeInTreeData(node, targetId) (递归调用)
     *       └── _cleanupNodeVueInstance(node)
     *
     * 4. 错误处理链路
     * _renderNodeWithSlot() 渲染失败时
     *   └── _createErrorDisplayElement(title, message)
     *
     * _cleanupNodeVueInstance() 清理失败时
     *   └── console.error() 记录错误日志
     *
     * 5. 数据流向
     * props.data (TreeNode | FlatNode[])
     *   └── watch.data.handler()
     *       └── mindMap.setRootNodeData()
     *           └── 触发节点重新渲染
     *               └── _renderNodeWithSlot()
     */

    // #region 初始化相关

    /**
     * 初始化 MindMap 实例
     * @private
     */
    _initializeMindMap() {
      if (!this._checkInitializationRequirements()) {
        return;
      }

      const refContainer = this.$refs.container as HTMLElement;

      // 在创建新实例之前，确保销毁旧实例
      this._destroyMindMapInstance();

      try {
        // 创建配置选项并初始化 MindMap
        const options = this._createMindMapOptions();
        this.mindMap = new MindMap(this.data, refContainer, options);

        // 设置事件监听器
        this._setupEventListeners();
        // 在 MindMap 实例创建成功后立即设置节点销毁监听器
        this._setupNodeDestroyListener();

        // this.mindMap.on(MindMapEvents.CANVAS_DRAG, (event) => {
        //   this.$emit("canvas-drag", event);
        // });
      } catch (error) {
        console.error("[MindMap] 初始化思维导图失败:", error);
      }
    },

    /**
     * 创建调试选项
     * @private
     * @returns DebugOptions 对象
     */
    _createDebugOptions(): DebugOptions {
      return {
        rootContainerBgColor: "rgba(247, 216, 76, 0.1)",
        drawGridBackground: true,
      };
    },

    /**
     * 设置 MindMap 事件监听器
     * @private
     */
    _setupEventListeners() {
      if (!this.mindMap) return;

      // 布局更新事件
      this.mindMap.on(MindMapEvents.LAYOUT_UPDATED, (event) => {
        this.$emit("layout-updated", event);
      });

      // 节点折叠状态变化事件
      this.mindMap.on(MindMapEvents.NODE_COLLAPSE_CHANGED, (event) => {
        this.$emit("node-collapse-changed", event);
      });

      // 画布拖拽开始事件
      this.mindMap.on(MindMapEvents.CANVAS_DRAG_START, (event) => {
        this.$emit("canvas-drag-start", event);
      });

      // 画布拖拽事件
      this.mindMap.on(MindMapEvents.CANVAS_DRAG, (event) => {
        this.$emit("canvas-drag", event);
      });

      // 画布拖拽结束事件
      this.mindMap.on(MindMapEvents.CANVAS_DRAG_END, (event) => {
        this.$emit("canvas-drag-end", event);
      });

      // 缩放变化事件
      this.mindMap.on(MindMapEvents.ZOOM_CHANGE, (event) => {
        this.$emit("zoom-change", event);
      });

      // 容器尺寸变化事件
      this.mindMap.on(MindMapEvents.CONTAINER_RESIZE, (event) => {
        this.$emit("container-resize", event);
      });
    },

    /**
     * 检查初始化 MindMap 的必要条件
     * @private
     * @returns 是否满足初始化条件
     */
    _checkInitializationRequirements(): boolean {
      if (!this.$refs.container) {
        console.error("[MindMap] 容器引用未找到");
        return false;
      }
      return true;
    },

    /**
     * 创建 MindMap 实例的配置选项
     * @private
     * @returns MindMap 配置选项对象
     */
    _createMindMapOptions() {
      if (this.isDebug) {
        this.debugOptions = this._createDebugOptions();
      } else {
        this.debugOptions = null;
      }

      return {
        isHorizontal: this.direction === "horizontal",
        siblingSeparation: this.siblingSeparation,
        levelSeparation: this.levelSeparation,
        initialOffset: this.initialOffset,
        lineShape: this.lineStyle as LineShapeType,
        contentCallback: this._createNodeContentCallback(),
        align: this.align as MindMapAlign,
        style: this._createMindMapStyle(),
        dragOptions: this.dragOptions,
        zoomOptions: this.zoomOptions,
        debugOptions: this.debugOptions ?? undefined,
      };
    },

    /**
     * 创建思维导图样式配置
     * @private
     * @returns 样式配置对象
     */
    _createMindMapStyle(): MindMapStyle {
      return {
        lineWidth: this.lineWidth,
        lineColor: this.lineColor,
        nodeBorderWidth: this.nodeBorderWidth,
        nodeBgColor: this.nodeBgColor,
        nodeBorderColor: this.nodeBorderColor,
      };
    },

    /**
     * 创建节点内容渲染的回调函数
     * @private
     * @returns 处理节点内容生成的回调函数
     */
    _createNodeContentCallback(): (node: NodeLike) => HTMLElement {
      return (node: NodeLike): HTMLElement => {
        // 修复插槽检测逻辑
        if (
          this.$scopedSlots.default ||
          (this.$slots.default && this.$slots.default.length > 0)
        ) {
          return this._renderNodeWithSlot(node);
        } else {
          console.warn(
            `[MindMap] 未找到默认插槽，节点 (ID: ${
              node.id || "未知"
            }) 使用默认渲染`
          );
          return this._renderDefaultNode(node);
        }
      };
    },

    // #endregion 初始化相关

    // #region 节点渲染相关

    /**
     * 使用默认插槽中的内容直接渲染节点（带缓存机制）
     * @private
     * @param node 节点数据
     * @returns 生成的 HTML 元素
     */
    _renderNodeWithSlot(node: NodeLike): HTMLElement {
      try {
        const nodeId = this._getOrCreateNodeId(node);

        // 检查是否已经有缓存的 Vue 实例
        let vueInstance = this.nodeIdToVueInstanceMap.get(nodeId);

        if (!vueInstance) {
          // 创建新的 Vue 实例来渲染插槽内容
          const VueConstructor = Vue.extend({
            parent: this,
            render: (h): VNode => {
              // 使用作用域插槽或默认插槽渲染节点内容
              if (this.$scopedSlots.default) {
                const slotContent = this.$scopedSlots.default({ node });
                // 确保返回单个 VNode，如果是数组则包装在 div 中
                if (Array.isArray(slotContent)) {
                  return h("div", slotContent);
                }
                return slotContent || h("div", `节点 ${nodeId}`);
              } else if (
                this.$slots.default &&
                this.$slots.default.length > 0
              ) {
                return h("div", this.$slots.default);
              } else {
                return h("div", `节点 ${nodeId}`);
              }
            },
          });

          vueInstance = new VueConstructor();

          // 缓存 Vue 实例
          this.nodeIdToVueInstanceMap.set(nodeId, vueInstance);
        }

        // 挂载 Vue 实例并获取生成的 DOM 元素
        const mountedInstance = vueInstance.$mount();
        const element = mountedInstance.$el as HTMLElement;

        if (!element) {
          throw new Error("Vue 实例挂载后未生成有效的 DOM 元素");
        }

        // 为元素添加包装类名以便识别
        if (!element.classList.contains(NODE_INNER_WRAPPER_CLASS)) {
          element.classList.add(NODE_INNER_WRAPPER_CLASS);
        }

        return element;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "未知错误";
        console.error(
          `[MindMap] 渲染节点插槽内容失败 (ID: ${node.id || "未知"}):`,
          error
        );

        // 渲染失败时返回错误显示元素
        return this._createErrorDisplayElement(
          "插槽渲染失败",
          `节点 ID: ${node.id || "未知"} - ${errorMessage}`
        );
      }
    },

    /**
     * 渲染默认节点内容
     * @private
     * @param node 节点数据
     * @returns 生成的 HTML 元素
     */
    _renderDefaultNode(node: NodeLike): HTMLElement {
      console.warn(
        `[MindMap] 未提供插槽内容或渲染失败，节点 (ID: ${
          node.id || "未知"
        }) 使用默认渲染`
      );
      const defaultElement = document.createElement("div");
      defaultElement.style.padding = "8px";
      defaultElement.style.border = "1px solid #ccc";
      defaultElement.style.borderRadius = "4px";
      const nodeLabel = `节点 ${node.id || "默认"}`;
      defaultElement.textContent = nodeLabel;
      return defaultElement;
    },

    /**
     * 为没有 ID 的节点生成唯一标识符
     * @private
     * @param node 节点数据
     * @returns 节点的 ID 或生成的唯一 ID
     */
    _getOrCreateNodeId(node: NodeLike): string {
      if (node.id !== undefined && node.id !== null) {
        return String(node.id);
      }

      // 为节点生成一个唯一 ID，使用时间戳和随机数组合
      const uniqueId = `temp-node-${Date.now()}-${getRamdomStr("*********")}`;

      // 可选：将生成的 ID 附加到节点对象上，如果它是可变的
      // 注意：如果节点对象是不可变的，这可能会失败
      try {
        (node as any).id = uniqueId;
      } catch (e) {
        console.warn("[MindMap] 无法为节点添加临时ID", e);
      }

      return uniqueId;
    },

    // #endregion 节点渲染相关

    // #region 错误处理相关

    /**
     * 创建一个用于显示错误的 HTML 元素
     * @private
     * @param title 错误标题
     * @param message 错误信息
     * @returns HTMLDivElement
     */
    _createErrorDisplayElement(title: string, message: string): HTMLElement {
      const errorEl = document.createElement("div");
      errorEl.style.padding = "8px";
      errorEl.style.border = "1px dashed red";
      errorEl.style.backgroundColor = "#ffeeee";
      errorEl.style.color = "red";
      errorEl.innerHTML = `${title}<br><small style="color: #cc0000;">${message}</small>`;
      return errorEl;
    },

    // #endregion 错误处理相关

    // #region 清理相关

    /**
     * 销毁当前的 MindMap 实例
     * @private
     */
    _destroyMindMapInstance() {
      if (this.mindMap) {
        // MindMap 实例的 destroy 方法应负责清理其创建的所有资源
        this.mindMap.destroy();
        this.mindMap = null;
      }

      // 手动清理所有缓存的 Vue 实例（防止内存泄漏）
      // 即使 _setupNodeDestroyListener 会处理单个节点的清理，
      // 在组件销毁时我们仍需要确保所有实例都被清理
      this._clearAllCachedVueInstanceMap();
    },

    /**
     * 监听节点销毁事件
     * @private
     */
    _setupNodeDestroyListener() {
      if (this.mindMap) {
        this.mindMap.on(
          MindMapEvents.NODE_DESTROYED,
          (event: MindMapNodeDestroyEvent) => {
            // 检查节点是否真正需要清理 Vue 实例
            this._handleNodeDestroy(event.node);
          }
        );
      }
    },

    /**
     * 处理节点销毁事件，仅在节点真正被删除时清理 Vue 实例
     * @private
     * @param node 被销毁的节点数据
     */
    _handleNodeDestroy(node: NodeLike) {
      const nodeId = node.id;
      if (!nodeId) return;

      // 检查该节点是否仍在当前数据中存在
      const stillExists = this._checkNodeExistsInCurrentData(String(nodeId));

      if (!stillExists) {
        // 只有当节点在当前数据中不存在时，才清理对应的 Vue 实例
        this._cleanupNodeVueInstance(node);

        if (this.isDebug) {
          console.log(
            `[MindMap] 节点 (ID: ${nodeId}) 已从数据中移除，清理 Vue 实例`
          );
        }
      } else {
        if (this.isDebug) {
          console.log(
            `[MindMap] 节点 (ID: ${nodeId}) 仅从 DOM 中移除，保留 Vue 实例以便复用`
          );
        }
      }
    },

    /**
     * 清理节点对应的 Vue 实例
     * @private
     * @param node 被销毁的节点数据
     */
    _cleanupNodeVueInstance(node: NodeLike) {
      // 检查节点是否有 ID
      const nodeId = node.id;
      if (!nodeId) return;

      // 尝试从映射表中获取 Vue 实例
      const vueInstance = this.nodeIdToVueInstanceMap.get(String(nodeId));
      if (vueInstance) {
        // 销毁 Vue 实例
        try {
          vueInstance.$destroy();
          if (process.env.NODE_ENV !== "production") {
            console.log(`[MindMap] 已销毁节点 (ID: ${nodeId}) 的 Vue 实例`);
          }
        } catch (e) {
          console.error(`[MindMap] 销毁节点 Vue 实例时出错:`, e);
        }

        // 从映射表中移除
        this.nodeIdToVueInstanceMap.delete(String(nodeId));
      }
    },

    /**
     * 清理所有缓存的 Vue 实例映射表
     * @private
     */
    _clearAllCachedVueInstanceMap() {
      if (this.nodeIdToVueInstanceMap.size === 0) return;

      this.nodeIdToVueInstanceMap.forEach((vueInstance, nodeId) => {
        try {
          if (vueInstance) {
            vueInstance.$destroy();
            if (process.env.NODE_ENV !== "production") {
              console.log(
                `[MindMap] 已清理缓存的节点 (ID: ${nodeId}) Vue 实例`
              );
            }
          }
        } catch (e) {
          console.error(`[MindMap] 清理缓存 Vue 实例时出错:`, e);
        }
      });
      this.nodeIdToVueInstanceMap.clear();
    },

    /**
     * 检查指定 ID 的节点是否仍存在于当前数据中
     * @private
     * @param nodeId 要检查的节点 ID
     * @returns 节点是否存在
     */
    _checkNodeExistsInCurrentData(nodeId: string): boolean {
      if (!this.data) return false;

      if (Array.isArray(this.data)) {
        // FlatNode 数组的情况
        return this.data.some((node) => String(node.id) === nodeId);
      } else {
        // TreeNode 对象的情况，需要递归查找
        return this._findNodeInTreeData(this.data, nodeId);
      }
    },

    /**
     * 在树形数据中递归查找指定 ID 的节点
     * @private
     * @param node 当前检查的节点
     * @param targetId 目标节点 ID
     * @returns 是否找到目标节点
     */
    _findNodeInTreeData(node: TreeNode, targetId: string): boolean {
      // 检查当前节点
      if (String(node.id) === targetId) {
        return true;
      }

      // 递归检查子节点
      if (node.children && node.children.length > 0) {
        return node.children.some((child) =>
          this._findNodeInTreeData(child, targetId)
        );
      }

      return false;
    },

    // #endregion 清理相关
  },
});
</script>

<style lang="scss" scoped>
.mind-map {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}
</style>